import { Environment } from './environment';
import { IndexDBDialogMessenger, IndexDBDialogMessengerName } from './indexDBDialogMessenger';
import { LocalStorageDialogMessenger, LocalStorageDialogMessengerName } from './localStorageDialogMessenger';
import { WrapperDialogMessenger } from './wrapperDialogMessenger';
import { DialogMessenger } from './dialogMessenger';
import { getUrlParameterByName } from '@yasoonofficial/runtime';
import { OfficeEventDialogMessenger, OfficeEventDialogMessengerName } from './officeEventDialogMessenger';

export type OfficeDialogLocation = 'host' | 'dialog';

const checkMessenger = async (messenger: DialogMessenger): Promise<boolean> => {
    let testValue = 'value' + new Date().toISOString();
    try {
        messenger.setData('yasoonTestStorage', testValue);
        let value = await messenger.getData('yasoonTestStorage');
        return (value === testValue);
    } catch (e) {
        console.log('Messenger ' + messenger.getName() + ' does not work here');
        return false;
    }
};

const messengerFactory = async (location: OfficeDialogLocation): Promise<DialogMessenger> => {
    console.log('[MessengerFactory] Creating messenger for location:', location);
    // Before we start determining anything, check if we are running in COM add-in
    if (Environment.isWrapperAddin()) {
        console.log('[MessengerFactory] Using WrapperDialogMessenger');
        return new WrapperDialogMessenger();
    }

    // Next: Check if we have been provided with the concrete name of the messenger
    const messengerTypeFromUrl = getUrlParameterByName("messengerType");
    const platformFromUrl = getUrlParameterByName("platform");
    console.log('[MessengerFactory] URL params:', { messengerTypeFromUrl, platformFromUrl });

    if (messengerTypeFromUrl === IndexDBDialogMessengerName) {
        console.log('[MessengerFactory] Using IndexDBDialogMessenger from URL');
        return new IndexDBDialogMessenger();
    } else if (messengerTypeFromUrl === LocalStorageDialogMessengerName) {
        let needsLegacyTimer = !!(Environment.isIE(platformFromUrl, navigator.userAgent) || Environment.detectOldSafari(platformFromUrl, navigator.userAgent));
        console.log('[MessengerFactory] Using LocalStorageDialogMessenger from URL, needsLegacyTimer:', needsLegacyTimer);
        return new LocalStorageDialogMessenger(needsLegacyTimer);
    } else if (messengerTypeFromUrl === OfficeEventDialogMessengerName) {
        console.log('[MessengerFactory] Using OfficeEventDialogMessenger from URL');
        return new OfficeEventDialogMessenger(location);
    }

    // Now, assume we need to determine the correct one
    // => First check if we can use the "best" one, which comes without any hacks, but has
    //    requirements - see https://docs.microsoft.com/en-us/javascript/api/office/office.dialog?view=common-js-preview#office-office-dialog-messagechild-member(1)
    if (Office.context.requirements.isSetSupported('Mailbox', '1.9') &&
        Office.context.requirements.isSetSupported('DialogApi', '1.2')) {
        console.log('[MessengerFactory] Using OfficeEventDialogMessenger');
        return new OfficeEventDialogMessenger(location);
    }
    console.log('[MessengerFactory] Office requirements not met, falling back to storage-based messenger');

    let platform = Environment.getPlatform();

    let ua = navigator.userAgent;
    let isIE = Environment.isIE(platform, ua);
    //Users/ Admins can disable IndexDB as well as localStorage.
    //Usually we prefer LocalStorage.
    //But IE has issues keep it in sync across different Tabs... So we use IndexDB first there...
    if (isIE) {
        let indexDB = new IndexDBDialogMessenger();
        let result = await checkMessenger(indexDB);
        if (result) {
            return indexDB;
        }

        //IndexDb does not work --> Fallback to LocalStorage with Callback Timer
        let localMessenger = new LocalStorageDialogMessenger(true);
        result = await checkMessenger(localMessenger);
        if (result) {
            return localMessenger;
        }
    } else {
        let isOldSafari = Environment.detectOldSafari(platform, ua);
        console.log('[MessengerFactory] Non-IE browser, isOldSafari:', isOldSafari);

        let localMessenger = new LocalStorageDialogMessenger(isOldSafari);
        let result = await checkMessenger(localMessenger);
        if (result) {
            console.log('[MessengerFactory] Using LocalStorageDialogMessenger');
            return localMessenger;
        }

        //Not sure if this is even possible. Usually if localStorage is disabled, indexDB is disabled too...
        //Nevertheless, we can give it a try...
        let indexDB = new IndexDBDialogMessenger();
        result = await checkMessenger(indexDB);
        if (result) {
            console.log('[MessengerFactory] Using IndexDBDialogMessenger');
            return indexDB;
        }
    }

    console.log('[MessengerFactory] All messenger options failed');
    return null;
};

// We need to store this on window, because making it available via singleton
// does not seem to work with webpack as loader...
export function getMessenger(location: OfficeDialogLocation): Promise<DialogMessenger> {
    // Do not use something generic like "messenger" as property name, because
    // this might collide with other libraries -> we observed a broadcastchannel from microsoft in the messenger attribute in some cases
    if (!window["yasoon-messenger"])
        window["yasoon-messenger"] = messengerFactory(location);

    return window["yasoon-messenger"];
}
